// The one deadline.
//
// Promoted from the byte-identical `withDeadline` twins in
// `apps/runner/src/runner-log-ring.ts` and `apps/runner/src/tools/ci-log.ts`.
// Those two were already the good version: they clear the timer in a `.finally`,
// which the open-coded `Promise.race([work, new Promise(… setTimeout …)])` sites
// counted by `scripts/one-impl.test.ts` do not — every one of those leaves a live
// timer behind on the fast path, which is how a finished test suite fails to exit.

/**
 * Reject if `p` outlives `ms`, so a stalled hop can never hold a drain open.
 *
 * `hop` names the operation in the error, because the whole value of a deadline
 * is arriving at a message that says which hop stalled. A bare "timeout" costs
 * the next debugger the bisect this parameter exists to skip.
 *
 * The timer is cleared on BOTH exits, and `unref`ed so a pending deadline never
 * by itself keeps a process alive. Losing the race does not cancel `p` — this
 * bounds the WAIT, not the work; a caller that must also stop the work passes an
 * `AbortSignal` to whatever produced `p`.
 */
export function withDeadline<T>(p: Promise<T>, ms: number, hop: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new DeadlineExceededError(hop, ms)), ms);
    (timer as { unref?: () => void }).unref?.();
  });
  return Promise.race([p, deadline]).finally(() => {
    if (timer !== undefined) clearTimeout(timer);
  });
}

/** What {@link withDeadline} rejects with — typed so a caller can tell it from the work's own failure. */
export class DeadlineExceededError extends Error {
  readonly hop: string;
  readonly ms: number;
  constructor(hop: string, ms: number) {
    super(`${hop} exceeded its ${ms}ms deadline`);
    this.name = 'DeadlineExceededError';
    this.hop = hop;
    this.ms = ms;
  }
}
